import React from 'react';
import { useUserColors } from '../../context/UserColorsContext';

// Personal colour helpers: a dot before names, initials chips, and text colour
// that stays readable on any palette colour.

export function initials(name) {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// WCAG relative luminance of a hex colour (null when it isn't one)
export function luminance(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const lin = (c) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
  return 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
}

// WCAG contrast ratio of two hex colours (1–21)
export function contrastRatio(a, b) {
  const x = luminance(a);
  const y = luminance(b);
  if (x == null || y == null) return 1;
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

// Dark or white text on a hex background: whichever contrasts more; pure black
// for the few mid-tone colours where neither reaches 4.5:1
export function textOn(hex) {
  if (luminance(hex) == null) return '#1a1a2e';
  const white = contrastRatio(hex, '#ffffff');
  const dark = contrastRatio(hex, '#1a1a2e');
  if (Math.max(white, dark) >= 4.5) return white > dark ? '#ffffff' : '#1a1a2e';
  return white > contrastRatio(hex, '#000000') ? '#ffffff' : '#000000';
}

export function ColorDot({ color, userId, size = 10, title, style }) {
  const { colorOf } = useUserColors();
  const c = color || colorOf(userId);
  return (
    <span
      aria-hidden={title ? undefined : 'true'}
      title={title}
      style={{
        display: 'inline-block', width: size, height: size, minWidth: size, borderRadius: '50%',
        background: c, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.18)', verticalAlign: 'middle', ...style,
      }}
    />
  );
}

// "● Name" — the dot carries the colour, the text stays dark
export function PersonName({ userId, name, color, size = 9, gap = 5, style, children }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap, minWidth: 0, ...style }}>
      <ColorDot userId={userId} color={color} size={size} />
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{children ?? name}</span>
    </span>
  );
}
